import { useMutation } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { OnboardingData } from '../features/onboarding/OnboardingWizard';
import type { UserSettings } from '../types/database';

const EDGE_FUNCTION_URL = import.meta.env.VITE_SUPABASE_URL + '/functions/v1';

// Fetch user settings
async function fetchUserSettings(): Promise<UserSettings | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('user_settings')
    .select('*')
    .eq('user_id', user.id)
    .single();

  if (error) {
    console.error('Error fetching user settings:', error);
    return null;
  }

  return data;
}

// Improve narration using AI
export function useImproveNarration() {
  return useMutation({
    mutationFn: async ({
      narration,
      tone = 'professional',
      language = 'Croatian',
    }: {
      narration: string;
      tone?: string;
      language?: string;
    }) => {
      // Fetch user settings to get the selected model
      const settings = await fetchUserSettings();
      const model = settings?.generate_script_model;

      const { data, error } = await supabase.functions.invoke('generate-script', {
        body: { narration, tone, language, model },
      });

      if (error) throw error;
      return data as { original: string; improved: string };
    },
  });
}

// Generate image using AI
export function useGenerateImage() {
  return useMutation({
    mutationFn: async ({
      prompt,
      aspectRatio = '16:9',
    }: {
      prompt: string;
      aspectRatio?: '1:1' | '3:4' | '4:3' | '9:16' | '16:9';
    }) => {
      // Fetch user settings to get the selected model
      const settings = await fetchUserSettings();
      const model = settings?.generate_image_model;

      const { data, error } = await supabase.functions.invoke('generate-image', {
        body: { prompt, aspectRatio, model },
      });

      if (error) throw error;
      return data as { 
        prompt: string; 
        aspectRatio: string; 
        finalImageUrl: string;
        model: string;
      };
    },
  });
}

// Generate speech using AI
export function useGenerateSpeech() {
  return useMutation({
    mutationFn: async ({
      text,
      language = 'hr-HR',
      voice = 'default',
    }: {
      text: string;
      language?: string;
      voice?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('generate-speech', {
        body: { text, language, voice },
      });

      if (error) throw error;
      return data as {
        text: string;
        language: string;
        voice: string;
        message: string;
        useFallback: boolean;
      };
    },
  });
}

// Generate full script from onboarding data
export function useGenerateFullScript() {
  return useMutation({
    mutationFn: async (onboardingData: OnboardingData) => {
      // Fetch user settings to get the selected model
      const settings = await fetchUserSettings();
      const model = settings?.generate_full_script_model;

      const { data, error } = await supabase.functions.invoke('generate-full-script', {
        body: { onboardingData, model },
      });

      if (error) throw error;
      return data as {
        scenes: Array<{
          name: string;
          shots: Array<{
            name: string;
            narration: string;
            durationSec: number;
            aiPrompt: string;
          }>;
        }>;
      };
    },
  });
}
